import { initSentry } from './utils/sentry';
initSentry(); // Initialize Sentry before React does anything

import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';

// scroll bar
import 'simplebar-react/dist/simplebar.min.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// global text scaling
import './styles/global-text-scaling.css';

// google-fonts
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/700.css';

import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';

import '@fontsource/public-sans/400.css';
import '@fontsource/public-sans/500.css';
import '@fontsource/public-sans/600.css';
import '@fontsource/public-sans/700.css';

// project import
import App from './App';
import { ConfigProvider } from 'contexts/ConfigContext';
import Loader from 'components/Loader';

const container = document.getElementById('root');
const root = createRoot(container!);

// MSAL imports
import { PublicClientApplication, EventType, EventMessage, AuthenticationResult } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalConfig } from "./authConfig";
import { EntraIdProvider } from 'contexts/EntraIdContext';
import { UserProvider } from 'contexts/UserContext';
import { SignalRProvider } from 'contexts/SignalRContext';
import { AuthErrorProvider } from 'components/auth/AuthErrorProvider';

// MUI X + Dayjs
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// ==============================|| MAIN - REACT DOM RENDER ||============================== //

// Create msalInstance once here
export const msalInstance = new PublicClientApplication(msalConfig);

// Load Version Check Test utilities (always available)
import('./utils/versionCheckTest').then((module) => {
  // Integrate into existing debug namespace
  if (typeof window !== 'undefined') {
    if (!(window as any).debug) {
      (window as any).debug = {};
    }
    
    // Add version testing utilities under debug.version
    (window as any).debug.version = {
      simulate: module.VersionCheckTest.simulateNewVersion,
      clearSimulation: module.VersionCheckTest.clearSimulatedVersion,
      getCurrentInfo: module.VersionCheckTest.getCurrentVersionInfo,
      forceCheck: module.VersionCheckTest.forceVersionCheck,
      clearCaches: module.VersionCheckTest.clearAllCaches,
      testFlow: module.VersionCheckTest.testVersionUpdateFlow,
      enableMonitoring: module.VersionCheckTest.enableVersionCheckMonitoring, // Deprecated
      enableVerbose: module.VersionCheckTest.enableVerboseLogging,
      disableVerbose: module.VersionCheckTest.disableVerboseLogging,
      setInterval: module.VersionCheckTest.setPollingInterval,
      resetInterval: module.VersionCheckTest.resetPollingInterval,
      getInterval: module.VersionCheckTest.getPollingInterval,
      analytics: {
        generate: module.VersionCheckTest.generateTestVersionData,
        inject: module.VersionCheckTest.injectTestVersionData,
        clear: module.VersionCheckTest.clearTestVersionData
      }
    };
  }
}).catch((error) => {
  console.warn('⚠️ Failed to load Version Check Test utilities:', error);
});

// Load Authentication Test Suite in test environments only
// This code is completely excluded from production builds
console.log('🔍 Auth Test Loading Check:', {
  DEV: import.meta.env.DEV,
  VITE_ENABLE_AUTH_TESTS: import.meta.env.VITE_ENABLE_AUTH_TESTS,
  hostname: window.location.hostname
});

if (import.meta.env.DEV || import.meta.env.VITE_ENABLE_AUTH_TESTS === 'true') {
  const hostname = window.location.hostname;
  const isTestEnv = hostname.includes('-tst') ||
                   hostname.includes('tst.') ||
                   hostname.includes('localhost');
  
  console.log('🔍 Auth Test Environment Check:', { hostname, isTestEnv });
  
  if (isTestEnv) {
    console.log('🔄 Attempting to load Authentication Test Suite...');
    import('./utils/authTestSuite').then(() => {
      console.log('🧪 Authentication Test Suite loaded for test environment');
    }).catch((error) => {
      console.warn('⚠️ Failed to load Authentication Test Suite:', error);
    });
  } else {
    console.log('🚫 Not a test environment - Auth Test Suite not loaded');
  }
} else {
  console.log('🚫 Auth Test loading conditions not met:', {
    DEV: import.meta.env.DEV,
    VITE_ENABLE_AUTH_TESTS: import.meta.env.VITE_ENABLE_AUTH_TESTS
  });
}

const RootComponent = () => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
        }
        msalInstance.addEventCallback((event: EventMessage) => {
          if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const payload = event.payload as AuthenticationResult;
            msalInstance.setActiveAccount(payload.account);
          }
        });
        setIsInitialized(true);
      } catch (error) {
        console.error('MSAL initialization failed', error);
      }
    };

    initializeMsal();
  }, []);

  if (!isInitialized) {
    return <Loader />;
  }

  return (
    <ConfigProvider>
      <MsalProvider instance={msalInstance}>
        <EntraIdProvider msalInstance={msalInstance}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <UserProvider>
              <AuthErrorProvider>
                <SignalRProvider>
                  <App />
                </SignalRProvider>
              </AuthErrorProvider>
            </UserProvider>
          </LocalizationProvider>
        </EntraIdProvider>
      </MsalProvider>
    </ConfigProvider>
  );
};

root.render(<RootComponent />);

